import {Component, computed, inject, Injector, input, model, OnInit, signal, Signal, ChangeDetectionStrategy} from "@angular/core"
import {CM_LOGGER_TEMPLATES} from "@app/common/services/logging"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from "@angular/material/dialog"
import {MatFormFieldModule} from "@angular/material/form-field"
import {MatIconModule} from "@angular/material/icon"
import {MatInputModule} from "@angular/material/input"
import {MatSelectModule} from "@angular/material/select"
import {OrganizationSelectComponent} from "@common/components/inputs/select/organization-select/organization-select.component"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {DropFilesComponent} from "@common/components/files"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {StringInputComponent} from "@common/components/inputs/string-input/string-input.component"
import {PermissionsService} from "@app/common/services/permissions/permissions.service"
import {toObservable} from "@angular/core/rxjs-interop"
import {z} from "zod"
import {MatCheckboxModule} from "@angular/material/checkbox"
import {MatChipsModule} from "@angular/material/chips"
import {AddItemDialogComponent} from "@app/common/components/dialogs/add-item-dialog/add-item-dialog.component"
import {MutationCreateTemplateInput, TemplateState, TemplateType} from "@generated"
import {StateLabel} from "@app/platform/models/state-labels"
import {MatRadioModule} from "@angular/material/radio"
import {Accept} from "@app/common/helpers/utils/files"
import {CheckboxComponent} from "@app/common/components/inputs/checkbox/checkbox.component"
import {RadioButtonComponent} from "@app/common/components/inputs/radio-button/radio-button.component"
import {RadioGroupComponent} from "@app/common/components/inputs/radio-group/radio-group.component"
import {NumericInputComponent} from "@app/common/components/inputs/numeric-input/numeric-input.component"
import {HintComponent} from "@common/components/hint/hint.component"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {MeshConversionError, MeshMissingUvsError, uploadMeshToGroup} from "@app/template-editor/helpers/upload"
import {UploadGqlService} from "@app/common/services/upload/upload.gql.service"
import {WebAssemblyWorkerService} from "@app/template-editor/services/webassembly-worker.service"
import {
    AddTemplateCreateTemplateGQL,
    AddTemplateCreateTemplateRevisionGQL,
    AddTemplateDeleteTemplateGQL,
    AddTemplateDeleteTemplateRevisionGQL,
    AddTemplateResultFragment,
    AddTemplateUpdateTemplateRevisionGQL,
} from "@app/templates/add-template-dialog/add-template.dialog.generated"
import {TemplateNodes, TemplateGraph} from "@cm/template-nodes"

export type TemplateCreateMethod = "BLANK" | "FROM_FBX_OBJ_PLY" | "FROM_GLTF_USDZ"

export type CreateTemplateFormData = {
    name: string
    organizationId?: string
    templateType: TemplateType
    templateState: TemplateState
    createMethod: TemplateCreateMethod
    consolidate: boolean
    scaleFactor: number
    scaleUv0: boolean
    centerOrigin: boolean
    convertToWorldSpaceUvs: boolean
    meshFile?: {file: File}
}

const AddTemplateSchema = z.discriminatedUnion("createMethod", [
    z.object({
        createMethod: z.literal("BLANK"),
        name: z.string().min(1, "Name is required"),
        organizationId: z.string().min(1, "Organization ID is required"),
        templateType: z.nativeEnum(TemplateType),
        templateState: z.nativeEnum(TemplateState),
    }),
    z.object({
        createMethod: z.literal("FROM_FBX_OBJ_PLY"),
        name: z.string().min(1, "Name is required"),
        organizationId: z.string().min(1, "Organization ID is required"),
        templateType: z.nativeEnum(TemplateType),
        templateState: z.nativeEnum(TemplateState),
        meshFile: z.object({
            file: z.instanceof(File),
        }),
        consolidate: z.boolean(),
        scaleFactor: z.number().transform((val) => (val === 1 ? undefined : val)),
        scaleUv0: z.boolean(),
        centerOrigin: z.boolean(),
        convertToWorldSpaceUvs: z.boolean(),
    }),
    z.object({
        createMethod: z.literal("FROM_GLTF_USDZ"),
        name: z.string().min(1, "Name is required"),
        organizationId: z.string().min(1, "Organization ID is required"),
        templateType: z.nativeEnum(TemplateType),
        templateState: z.nativeEnum(TemplateState),
        meshFile: z.object({
            file: z.instanceof(File),
        }),
        consolidate: z.boolean(),
        scaleFactor: z.number().transform((val) => (val === 1 ? undefined : val)),
        scaleUv0: z.boolean(),
        centerOrigin: z.boolean(),
        convertToWorldSpaceUvs: z.boolean(),
    }),
])

export type AddTemplateData = z.infer<typeof AddTemplateSchema>

enum DefaultScaleOptions {
    cm = "cm",
    m = "m",
    mm = "mm",
    custom = "custom",
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-add-template-dialog",
    imports: [
        FormsModule,
        MatIconModule,
        MatChipsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        MatButtonModule,
        MatCheckboxModule,
        MatRadioModule,
        AddItemDialogComponent,
        CheckboxComponent,
        DropFilesComponent,
        InputContainerComponent,
        NumericInputComponent,
        OrganizationSelectComponent,
        RadioButtonComponent,
        RadioGroupComponent,
        StringInputComponent,
        HintComponent,
    ],
    templateUrl: "./add-template-dialog.component.html",
    styleUrl: "./add-template-dialog.component.scss",
})
export class AddTemplateDialogComponent implements OnInit {
    private readonly logger = inject(CM_LOGGER_TEMPLATES)
    readonly $templateType = input<TemplateType>(TemplateType.Product, {alias: "templateType"})
    readonly $templateTypeName = input<string>("template", {alias: "templateTypeName"})
    readonly $templateTypeLabels = input<Map<TemplateType, StateLabel<TemplateType>>>(new Map(), {alias: "templateTypeLabels"})
    readonly $sortedTemplateTypeLabels: Signal<StateLabel<TemplateType>[]> = computed(() => {
        return Array.from(this.$templateTypeLabels().values()).sort((a, b) => a.label.localeCompare(b.label))
    })

    readonly $currentTemplateTypeName = computed(() => {
        const currentType = this.$data().templateType
        switch (currentType) {
            case TemplateType.Part:
                return "part"
            case TemplateType.Picture:
                return "picture"
            case TemplateType.Product:
                return "product"
            case TemplateType.Scene:
                return "scene"
            case TemplateType.System:
                return "system template"
        }
    })

    protected readonly permission = inject(PermissionsService)
    readonly organizations = inject(OrganizationsService)
    readonly injector = inject(Injector)
    private readonly dialogRef = inject<MatDialogRef<AddTemplateDialogComponent, unknown>>(MatDialogRef)
    private readonly dialogData = inject<{templateType?: TemplateType}>(MAT_DIALOG_DATA, {optional: true})
    private readonly notifications = inject(NotificationsService)
    private readonly createTemplateGql = inject(AddTemplateCreateTemplateGQL)
    private readonly createTemplateRevisionGql = inject(AddTemplateCreateTemplateRevisionGQL)
    private readonly updateTemplateRevisionGql = inject(AddTemplateUpdateTemplateRevisionGQL)
    private readonly deleteTemplateGql = inject(AddTemplateDeleteTemplateGQL)
    private readonly deleteTemplateRevisionGql = inject(AddTemplateDeleteTemplateRevisionGQL)
    private readonly uploadService = inject(UploadGqlService)
    private readonly workerService = inject(WebAssemblyWorkerService)

    $can = this.permission.$to

    readonly $data = model<CreateTemplateFormData>(
        {
            name: "",
            organizationId: this.organizations.$current()?.id,
            createMethod: "FROM_FBX_OBJ_PLY",
            templateType: TemplateType.Product,
            templateState: TemplateState.Draft,
            consolidate: false,
            centerOrigin: true,
            convertToWorldSpaceUvs: false,
            scaleFactor: 1,
            scaleUv0: false,
        },
        {alias: "data"},
    )
    data$ = toObservable(this.$data)

    readonly $isLoading = signal<boolean>(false)
    readonly $meshError = signal<string | null>(null)

    readonly $scaleFactorSelection = signal<DefaultScaleOptions>(DefaultScaleOptions.cm)

    DefaultScaleOptions = DefaultScaleOptions
    protected readonly Accept = Accept

    readonly $validatedInputData = computed(() => {
        const data = this.$data()
        const validatedData = AddTemplateSchema.safeParse(data)
        if (!validatedData.success) {
            return undefined
        }
        return validatedData.data
    })

    readonly $createMethods = signal<{id: TemplateCreateMethod; label: string; icon: string}[]>([
        {
            id: "BLANK",
            label: "Blank",
            icon: "fa-file",
        },
        {
            id: "FROM_FBX_OBJ_PLY",
            label: "From .fbx/.obj/.ply",
            icon: "fa-cube",
        },
        // {
        //     id: "FROM_GLTF_USDZ",
        //     label: "From .gltf/.usdz",
        // },
    ])

    ngOnInit(): void {
        // Can't use input signal as default value in the model constructor
        const templateType = this.dialogData?.templateType ?? this.$templateType()
        this.updateData({templateType})
    }

    updateData = (updateData: Partial<CreateTemplateFormData>) => {
        this.$data.update((prev) => ({
            ...prev,
            ...updateData,
        }))
    }

    updateOrganization = async (data: {organizationId: string}) => {
        this.updateData({organizationId: data.organizationId})
    }

    updateMeshFile = async ({file}: {file?: File}) => {
        this.$meshError.set(null)
        this.$data.update((prev) => ({
            ...prev,
            meshFile: file ? {file} : undefined,
        }))
    }

    updateScaleFactor = (scaleFactor: DefaultScaleOptions) => {
        this.updateData({scaleFactor: this.getScaleFactor(scaleFactor)})
    }

    private getScaleFactor = (scaleFactor: DefaultScaleOptions) => {
        switch (scaleFactor) {
            case DefaultScaleOptions.cm:
                return 1.0
            case DefaultScaleOptions.m:
                return 100.0
            case DefaultScaleOptions.mm:
                return 0.01
            case DefaultScaleOptions.custom:
                return this.$data().scaleFactor
        }
    }

    onConfirm = async () => {
        const input = this.$validatedInputData()
        if (!input) return

        this.$isLoading.set(true)
        this.dialogRef.disableClose = true
        let created: AddTemplateResultFragment | undefined
        let createdRevisionId: string | undefined
        try {
            switch (input.createMethod) {
                case "BLANK": {
                    created = await this.createTemplate({
                        name: input.name,
                        organizationId: input.organizationId!,
                        templateType: input.templateType,
                        state: input.templateState,
                    })
                    break
                }
                case "FROM_FBX_OBJ_PLY": {
                    created = await this.createTemplate({
                        name: input.name,
                        organizationId: input.organizationId!,
                        templateType: input.templateType,
                        state: input.templateState,
                    })
                    const {createTemplateRevision} = await mutateThrowingErrors(this.createTemplateRevisionGql)({
                        input: {templateId: created.id, completed: false, graph: null},
                    })
                    createdRevisionId = createTemplateRevision.id
                    const group = await uploadMeshToGroup({
                        injector: this.injector,
                        uploadService: this.uploadService,
                        workerService: this.workerService,
                        file: input.meshFile!.file,
                        organizationLegacyId: created.organization.legacyId,
                        templateRevisionId: createTemplateRevision.id,
                        meshConversionOptions: {
                            consolidate: input.consolidate,
                            center: input.centerOrigin,
                            convertToWorldSpaceUvs: input.convertToWorldSpaceUvs,
                            resolution: 0.0001,
                            scaleFactor: input.scaleFactor,
                            scaleUv0: input.scaleUv0,
                        },
                    })
                    if (group === false) throw new Error("Mesh conversion and upload failed")
                    const graph = new TemplateGraph({parameters: {name: "Template Graph", nodes: new TemplateNodes({parameters: {list: []}})}})
                    graph.parameters.nodes.addEntry(group)
                    await mutateThrowingErrors(this.updateTemplateRevisionGql)({
                        input: {id: createTemplateRevision.id, graph: graph.serialize()},
                    })

                    break
                }
                case "FROM_GLTF_USDZ": {
                    throw new Error("GLTF/USDZ import not yet supported")
                }
            }
        } catch (err) {
            // Cleanup created template/revision on any error
            if (created) {
                try {
                    if (createdRevisionId) {
                        await mutateThrowingErrors(this.deleteTemplateRevisionGql)({id: createdRevisionId})
                    }
                    await mutateThrowingErrors(this.deleteTemplateGql)({id: created.id})
                } catch (cleanupErr) {
                    this.logger.error("Error cleaning up template after failure:", cleanupErr)
                }
            }

            if (err instanceof MeshMissingUvsError || err instanceof MeshConversionError) {
                this.$meshError.set(err.message)
                this.$isLoading.set(false)
                this.dialogRef.disableClose = false
                return
            }

            this.logger.error("Error while creating template:", err)
            const errorMessage = err instanceof Error ? err.message : "Error while creating template."
            this.notifications.showError(errorMessage)
            this.$isLoading.set(false)
            this.dialogRef.disableClose = false
            return
        }
        this.$isLoading.set(false)
        this.dialogRef.disableClose = false
        this.dialogRef.close(created)
    }

    private async createTemplate(data: MutationCreateTemplateInput): Promise<AddTemplateResultFragment> {
        const {createTemplate} = await mutateThrowingErrors(this.createTemplateGql)({input: data})
        return createTemplate
    }
}
